import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, RadioGroup } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { AssignablePartnerListsDocument } from '../../../graphql/__generated__/types';
import { partnerListEmptiness } from './catalogue';
import { Draft } from './draft';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
}

// The "no list" choice, pre-selected, mapping to partnerListId: null. A radio cannot be unticked.
const NO_LIST = '';

/**
 * Step 4: the partner list, an optional attribution. One list at most (InviteUserInput carries a
 * single partnerListId), and no grouping by account: the habilitation hangs from the person.
 */
const PartnerListStep = ({ draft, onChange }: Props) => {
    const { data, loading, error, refetch } = useQuery(AssignablePartnerListsDocument);

    const lists = (data?.assignablePartnerLists?.lists ?? []).filter(Boolean);
    const emptiness = partnerListEmptiness(error, data?.assignablePartnerLists?.emptyReason);

    // The count is rows of the list; zero is shown because granting an empty list opens an empty module.
    const options = [
        {
            value: NO_LIST,
            label: c('Onboarding partners').t`No list — the « Partenaires » module stays empty for this person`,
        },
        ...lists.map((list) => {
            const count = list.partnerCount ?? 0;
            const name = list.name ?? '';
            const size =
                count === 0
                    ? c('Onboarding partners').t`no partner yet`
                    : ngettext(msgid`${count} partner`, `${count} partners`, count);
            return { value: list.id as string, label: `${name} · ${size}` };
        }),
    ];

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding partners')
                    .t`Associate a partner list`}</h2>
            </div>

            {loading && <ListSkeleton rows={3} withControl />}

            {!loading && emptiness && (
                <Alert type={emptiness.tone} title={emptiness.title}>
                    <p className="text-sm">{emptiness.guidance}</p>
                    {emptiness.detail && (
                        <p className="mt-2 break-words font-mono text-xs text-text-600">{emptiness.detail}</p>
                    )}
                    {error && (
                        <div className="mt-2">
                            <Button category="weak" size="small" onClick={() => refetch()}>
                                {c('Action').t`Retry`}
                            </Button>
                        </div>
                    )}
                </Alert>
            )}

            {/* The "no list" choice is offered even when the catalogue is empty: it is the answer being recorded. */}
            <RadioGroup
                name="partner-list"
                value={draft.partnerListId ?? NO_LIST}
                onValue={(value: string) => onChange({ partnerListId: value === NO_LIST ? null : value })}
                options={options}
            />
        </div>
    );
};

export default PartnerListStep;
